<template>
	<ValidationProvider 
		tag="div" 
		:vid="vid"
		:name="placeholderText" 
		:rules="rules" 
		v-slot="{ errors, required, ariaInput, ariaMsg }"
		mode="passive"
	>
		<div class="c-input-wrapper c-input-wrapper__text" :class="classes">
			<div v-if="label" class="c-input_label mb-2 d-flex justify-content-between" :class="{ 'c-input_label--inline': inlineLabel }">
				<label class="d-block font_14_19 font_roboto" :class="inlineLabel ? 'text-white mx-auto' : ''">{{ label }}{{ required ? '*' : '' }}</label>
				<slot name="right-label"></slot>
			</div>
			<div class="c-input" :class="{ '--with-prefix' : prefix, '--error' : errors[0] }">
				<input 
					class="c-input__field"
					:class="`c-input__field--${type}`"
					:name="name"
					v-model="currentValue" 
					:type="type" 
					:placeholder="placeholderText" 
					:disabled="disabled"
					:autocomplete="autocomplete"
					v-bind="ariaInput"
					@focus="focussed = true"
					@blur="focussed = false"
				/>
				<icon-clear 
					v-if="showClear && focussed && currentValue && currentValue != ''" 
					class="c-input__clear u-clickable"
					:color="clearIconColor"
					@click.native="clearValue" 
					@mouseover.native="clearIconColor = '#4B4B4B'"
					@mouseleave.native="clearIconColor = '#C4C4C4'"
				/>
				<span v-if="note" class="d-block text-grey font_14_19 mt-3">{{ note }}</span>
				<span v-if="prefix" class="c-input__prefix">{{ prefix }}</span>
			</div>
			<span 
				v-if="errors[0]" 
				class="text-error font_14_19"
				v-bind="ariaMsg"
			>
				{{ errors[0] }}
			</span>
		</div>
	</ValidationProvider>
</template>

<script>
import { ValidationProvider, configure } from 'vee-validate'

configure({
	classes: {
		valid: 'c-input-wrapper--valid',
		invalid: 'c-input-wrapper--invalid',
		dirty: 'c-input-wrapper--is-dirty', // multiple classes per flag!
		touched: 'c-input-wrapper--touched'
	}
})

export default {
	name: 'InputText',
	props: {
		label: {
			type: [String, Boolean],
			default: false
		},
		type: {
			type: String,
			default: 'text',
			validator(value) {
				return ["url", "text", "password", "tel", "search", "number", "email", "date"].includes(value);
			}
		},
		rules: {
			type: [String, Object],
			default: ''
		},
		disabled: {
			type: Boolean,
			default: false
		},
		value: String,
		error: {
			type: String
		},
		vid: {
			type: String,
			default: undefined
		},
		name: {
			type: [String, Boolean],
			default: ''
		},
		autocomplete: {
			type: [String, Boolean],
			default: false
		},
		init: {
			type: String,
			default: null
		},
		note: {
			type: String,
			default: null
		},
		placeholder: {
			type: [String, Boolean],
			default: false
		},
		classes: {
			type: String
		},
		last: {
			type: Boolean,
			default: false
		},
		inlineLabel: {
			type: Boolean,
			default: false
		},
		prefix: {
			type: [String, Boolean],
			default: false
		},
		showClear: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		currentValue: '',
		focussed: false,
		clearIconColor: '#C4C4C4'
	}),
	components: {
		ValidationProvider,
	},
	methods: {
		clearValue() {
			this.currentValue = null
		}
	},
	computed: {
		placeholderText() {
			return this.placeholder === false ? this.label : this.placeholder
		}
	},
	watch: {
		currentValue (val) {
			// allows us to use v-model on our input.
			this.$emit('input', val)
		},
		value (val) {
			this.currentValue = this.value
		}
	},
	mounted() {
		this.currentValue = this.value
	}
}
</script>

<style lang="scss">
	.c-input-wrapper {
		margin-bottom: 24px;
		position: relative;

		&--last {
			margin-bottom: 0;
		}

		label {
			color: $dark-grey;
		}
	}

	.c-input {
		position: relative;

		&__field { 
			border: 1px solid $grey;
			border-radius: 4px;
			width: 100%;
			color: $black;

			&:hover {
				border: 1px solid #0C9D61;
			}

			&:focus {
				border: 1px solid #0C9D61;
				box-shadow: 0px 0px 3px #57A651;
			}

			&:disabled {
				color: $light-grey;
				border: $light-grey;
			}
		}

		&__prefix {
			position: absolute;
			top: 8px;
			left: 10px;
			color: $ocean-blue;
		}

		&__clear {
			position: absolute;
			right: 19px;
			top: 11px;
		}

		&.--with-prefix {
			position: relative;

			.c-input__field {
				padding-left: 32px;
			}
		}

		&.--error {

			.c-input__field {
				border: 1px solid $error;
			}
		}
	}

	.c-input_label {
	
		&--inline {
			position: absolute;
			right: 0;
			width: 175px;
			margin: 0;
			height: 40px;
			text-align: center;
			background-color: $ocean-blue;
			z-index: 2;

			label {
				line-height: 42px !important;
			}
		}
	}

	.c-input-wrapper__text--alternate {
		margin-bottom: 15px;
		
		.c-input__field {
			border: 1px solid $ocean-blue;
			border-radius: 4px;
		}
	}

	::placeholder {
		color: $grey;
		opacity: 1;
	}

	
</style>